'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CircularProgress } from 'src/components/PulseUI/CircularProgress';
import ExportMenuItem from './ExportMenuItem';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import { useTable } from 'src/context/table';
import { Tooltip } from 'src/components';
import { twMerge } from 'tailwind-merge';

/**
 * GridExportMenu component for exporting grid data in various formats
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onExport - Callback function when export is triggered
 * @param {boolean} [props.isExporting=false] - Whether export is in progress
 * @param {boolean} [props.disabled=false] - Whether the export button is disabled
 * @param {string} [props.table=''] - Identifier for the table being exported
 */
interface GridExportMenuProps {
  onExport: (type: string) => void;
  isExporting?: boolean;
  disabled?: boolean;
  table?: string;
}

const GridExportMenu = ({
  onExport,
  isExporting = false,
  disabled = false,
  table = '',
}: GridExportMenuProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const { showIcon } = useTable();

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu on mobile view change
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [isMobile]);

  // Update menu position
  const updateMenuPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX,
      });
    }
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleExport = useCallback(
    (type: string) => {
      onExport(type);
      setIsOpen(false);
    },
    [onExport]
  );

  const toggleMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isOpen) {
        updateMenuPosition();
      }
      setIsOpen(!isOpen);
    },
    [isOpen, updateMenuPosition]
  );

  const tooltipContent = disabled ? (
    <div className="text-sm max-w-xs">
      Export is disabled for datasets with more than 5,000 records
    </div>
  ) : null;

  const showIconButton = isMobile || showIcon;

  return (
    <div className="relative">
      <Tooltip content={tooltipContent}>
        <button
          ref={triggerRef}
          id={showIconButton ? `${table}-export-menu-icon-button` : `${table}-export-menu-menu-button`}
          data-testid={showIconButton ? `${table}-export-menu-icon-button` : `${table}-export-menu-menu-button`}
          type="button"
          onClick={toggleMenu}
          disabled={disabled}
          aria-label="Export data"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-controls={`${table}-export-menu`}
          className={twMerge(
            'inline-flex items-center justify-center gap-2 font-black',
            'bg-neutral-100 hover:bg-neutral-200 text-neutral-700',
            'rounded-md transition-colors',
            showIconButton ? 'p-2' : 'px-3 py-2',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isExporting ? (
            <CircularProgress size="sm" />
          ) : (
            <Icon icon={faDownload} className="text-sm" aria-hidden="true" />
          )}
          {!showIconButton && <span>Export</span>}
        </button>
      </Tooltip>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={`${table}-export-menu`}
            data-testid={`${table}-export-menu`}
            role="menu"
            aria-label="Export options"
            className="fixed z-[9999] min-w-[100px] max-h-[300px] overflow-auto bg-white rounded-lg shadow-lg border border-neutral-200 py-1"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              transform: 'translateX(-100%)',
            }}
          >
            <ExportMenuItem
              type="csv"
              onExport={handleExport}
              id={`${table}-export-menu-item-csv`}
              data-testid={`${table}-export-menu-item-csv`}
            />
            <ExportMenuItem
              type="xlsx"
              onExport={handleExport}
              id={`${table}-export-menu-item-xlsx`}
              data-testid={`${table}-export-menu-item-xlsx`}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default GridExportMenu;
